import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import HomeDashboard from "./pages/common/HomeDashboard";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { EmailProvider } from "./contexts/EmailContext";
import ForgotPassword from "./pages/auth/ForgotPassword";
import Communications from "./pages/advisor-portal/client-database/Communications";
import Clients from "./pages/advisor-portal/client-database/Clients";
import HouseholdProfile from "./pages/advisor-portal/client-database/HouseholdProfile";
import ClientProfile from "./pages/advisor-portal/client-database/ClientProfile";
import AnnualReviewProfile from "./pages/advisor-portal/client-database/annual-review/AnnualReviewProfile";
import EstatePlanning from "./pages/advisor-portal/client-database/EstatePlanning";
import PostAnnualReviewActionItems from "./pages/advisor-portal/client-database/annual-review/PostAnnualReviewActionItems";
import alternativeInvestmentRoutes from "./routes/alternativeInvestmentRoutes";
import TradingRebalancing from "./pages/portfolio-management/TradingRebalancing";
import WithdrawalsDistributions from "./pages/portfolio-management/WithdrawalsDistributions";
import FinancialPlanningAnalysisReports from "./pages/financial-planning/AnalysisReports";
import AdvisorPortalAnalysisReports from "./pages/advisor-portal/client-database/AnalysisReports";
import Banking from "./pages/financial-planning/Banking";
import RetirementJourney from "./pages/financial-planning/RetirementJourney";
import Campaigns from "./pages/marketing-and-sales/Campaigns";
import DesignHub from "./pages/marketing-and-sales/DesignHub";
import MarketingAnalytics from "./pages/marketing-and-sales/MarketingAnalytics";
import SocialMediaMarketing from "./pages/marketing-and-sales/SocialMediaMarketing";
import SalesCoach from "./pages/marketing-and-sales/SalesCoach";
import Investments from "./pages/advisor-portal/client-database/Investments";
import AdvisorPortalDashboard from "./pages/advisor-portal/AdvisorPortalDashboard";
import Calendar from "./pages/advisor-portal/Calendar";
import ClientOnboarding from "./pages/advisor-portal/ClientOnboarding";
import Email from "./pages/advisor-portal/Email";
import Messages from "./pages/advisor-portal/Messages";
import Events from "./pages/advisor-portal/events/Events";
import EventDetails from "./pages/advisor-portal/events/EventDetails";
import AllEvents from "./pages/advisor-portal/events/AllEvents";
import Settings from "./pages/administrative/Settings";
import PageUnderConstruction from "./pages/common/PageUnderConstruction";
import DocumentCenter from "./pages/advisor-portal/client-database/DocumentCenter";
import RelationshipManagement from "./pages/advisor-portal/client-database/RelationshipManagement";
import FinancialCalculators from "./pages/financial-planning/calculators/FinancialCalculators";
import RMDAnalysis from "./pages/financial-planning/calculators/RMDAnalysis";
import EfficientDistribution from "./pages/financial-planning/calculators/EfficientDistribution";
import Insurance from "./pages/financial-planning/insurance/Insurance";
import FinancialPlanningTaxPlanning from "./pages/financial-planning/tax-planning/TaxPlanning";
import AdvisorPortalTaxPlanning from "./pages/advisor-portal/client-database/TaxPlanning";
import Allocations from "./pages/portfolio-management/Allocations";
import MarketNews from "./pages/portfolio-management/MarketNews";
import MarketResearch from "./pages/portfolio-management/MarketResearch";
import PortfolioReports from "./pages/portfolio-management/PortfolioReports";
import Billing from "./pages/portfolio-management/Billing";
import FirmSettings from "./pages/administrative/FirmSettings/FirmSettings";
import HouseholdInformation from "./components/domains/advisor-portal/client-profile/HouseholdInformation";
import CareerEducation from "./components/domains/advisor-portal/client-profile/CareerEducation";
import HealthLife from "./components/domains/advisor-portal/client-profile/HealthLife";
import IncomeBenefits from "./components/domains/advisor-portal/client-profile/IncomeBenefits";
import ExpensesBudgets from "./components/domains/advisor-portal/client-profile/ExpensesBudgets";
import BalanceSheet from "./components/domains/advisor-portal/client-profile/BalanceSheet";
import GoalsLegacy from "./components/domains/advisor-portal/client-profile/GoalsLegacy";
import SummaryActionItems from "./components/domains/advisor-portal/client-profile/SummaryActionItems";
import { useOutletContext } from "react-router-dom";
import { EventProvider } from "./contexts/EventContext";
import { AISidebarProvider } from "./contexts/AISidebarContext";
import FirmView from "./pages/portfolio-management/allocations/FirmView";
import AdvisorView from "./pages/portfolio-management/allocations/AdvisorView";
import HouseholdView from "./pages/portfolio-management/allocations/HouseholdView";
import AccountView from "./pages/portfolio-management/allocations/AccountView";
import AUMUpload from "./pages/portfolio-management/allocations/AUMUpload";
import HouseholdDetailView from "./pages/portfolio-management/allocations/HouseholdDetailView";
import { DemoProvider, useDemo } from "./contexts/DemoContext";
import DemoSelectionModal from "./components/shared/DemoSelectionModal";
import DemoModal from "./components/shared/DemoModal";
import SpotlightOverlay from "./components/shared/SpotlightOverlay";
import { MODAL_TEMPLATES, MODAL_TYPES } from "./demo/annualReviewDemoLogic";
import AnnualReviewDemo from "./demos/AnnualReviewDemo";
import { ThemeProvider as ThemeContextProvider } from "./contexts/ThemeContext";
import ThemeProvider from "./theme/ThemeProvider";
import logger from './utils/logger';
import AppLayout from "./components/shared/AppLayout";

// Import Administrative components
import FirmOverview from "./pages/administrative/FirmOverview/FirmOverview";
import CompanyStructure from "./pages/administrative/CompanyStructure/CompanyStructure";
import Branding from "./pages/administrative/Branding/Branding";
import ComplianceCenter from "./pages/administrative/ComplianceCenter/ComplianceCenter";
import PayrollTax from "./pages/administrative/PayrollAndTax/PayrollTax";
import FirmAnalytics from "./pages/administrative/FirmAnalytics/FirmAnalytics";

// Import Education & Licensing components
import FinancialLiteracy from "./pages/education-and-licensing/FinancialLiteracy";
import FinancialNews from "./pages/education-and-licensing/FinancialNews";
import CurrentLicenses from "./pages/education-and-licensing/CurrentLicenses";
import InsuranceCoverage from "./pages/financial-planning/insurance/InsuranceCoverage";
import CERequirements from "./pages/education-and-licensing/CERequirements";

// Debug log to verify MODAL_TEMPLATES
console.log('🔍 [App.js] MODAL_TEMPLATES loaded:', {
  types: Object.keys(MODAL_TEMPLATES),
  showDebriefingRoom: MODAL_TEMPLATES[MODAL_TYPES.SHOW_DEBRIEFING_ROOM],
  hasComponent: !!MODAL_TEMPLATES[MODAL_TYPES.SHOW_DEBRIEFING_ROOM]?.component
});

// Create a client
const queryClient = new QueryClient();

// PrivateRoute component for route protection
const PrivateRoute = () => {
  const { token, isLoading } = useAuth();
  logger.info("🔒 PrivateRoute - Auth state:", { isLoading, hasToken: !!token });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return token ? <Outlet /> : <Navigate to="/" />;
};

const GoalsLegacyWrapper = () => {
  const { householdId, userId } = useParams();
  return <GoalsLegacy householdId={householdId} userId={userId} />;
};

// New wrapper component to preserve state
const HouseholdDetailViewWrapper = () => {
  const location = useLocation();
  const { householdId } = useParams();
  const { isDemoMode, demoStep } = useDemo();
  
  console.log('🔍 HouseholdDetailView Wrapper:', {
    locationState: location.state,
    isDemoMode,
    demoStep,
    householdId
  });
  
  return <HouseholdDetailView />;
};

// Component for demo playback modals only
const DemoModalContainer = () => {
  const {
    activeModal,
    activeSpotlight,
    onEffectComplete,
    onAction,
    transcript,
    isRecording,
    isPaused,
    addTranscriptLine,
    updateTranscript,
    currentStep,
    totalSteps,
    demoProgress,
    onModalAction,
    setState
  } = useDemo();
  const navigate = useNavigate();
  const location = useLocation();

  // Early return if no modal or spotlight to show
  if (!activeModal && !activeSpotlight) {
    return null;
  }

  // Only access modal properties if it exists
  const template = activeModal ? MODAL_TEMPLATES[activeModal.type] || {} : {};
  const { component: CustomComponent } = template;
  const nextModalType = template.nextModalType;
  const nextAction = activeModal ? (activeModal.props?.nextAction || template.nextAction) : null;
  
  console.log('💬 [DemoModalContainer] MODAL_TEMPLATES lookup:', {
    type: activeModal?.type,
    template,
    hasComponent: !!template.component,
    CustomComponent
  });

  // Primary button handler: handle modal-specific actions
  const handlePrimaryAction = () => {
    // 🚨 SPECIAL CASE: SMART_FOCUS
    if (activeModal?.type === 'SMART_FOCUS') {
      console.log('💬 [DemoModalContainer] SMART_FOCUS click — firing spotlight + advance');
      console.log('💬 SMART_FOCUS props:', activeModal.props);
      // 1) run the spotlight callback you passed in via props.onAction
      activeModal.props.onAction();
      // 2) advance the demo
      onAction({ type: 'ADVANCE_DEMO' });
      return;
    }

    try {
      console.log('💬 [DemoModalContainer] handling primary action:', {
        modalType: activeModal.type,
        nextModalType,
        nextAction,
        shouldBeginRecording: template.shouldBeginRecording,
        template
      });

      // Handle recording start if needed
      if (template.shouldBeginRecording) {
        console.log('🎯 Starting recording from modal');
        onAction({ type: 'START_RECORDING' });
        return;
      }

      // Handle navigation if needed
      if (template.targetPage) {
        if (template.useInternalNavigation) {
          navigate(template.targetPage, { 
            state: { 
              resumeDemo: nextAction || (nextModalType ? { type: nextModalType } : null),
              transcript,
              isRecording,
              isPaused,
              preserveSidebar: template.preserveSidebar,
              selectedJourney: 'Existing Client'
            } 
          });
        } else {
          window.location.href = template.targetPage;
        }
        return;
      }

      // Handle modal closing and next actions
      if (nextModalType || nextAction) {
        // Convert nextModalType to action format if present
        const actions = nextModalType 
          ? [{ type: nextModalType }]
          : (Array.isArray(nextAction) ? nextAction : [nextAction]);

        // If any action is SMART_FOCUS, don't auto-resume
        const isSmartFocus = actions.some(action => action.type === 'SMART_FOCUS');

        // Always close the modal first
        onModalAction(actions[0]);

        if (isSmartFocus) {
          // Let SMART_FOCUS handle pausing and resuming manually
          Promise.resolve().then(() => {
            actions.forEach(action => onAction(action));
          });
        }
      } else {
        // Just close the modal if no next action
        onAction({ type: 'CLOSE_MODAL' });
      }
    } catch (error) {
      console.error('❌ [DemoModalContainer] Error handling primary action:', error);
      // Ensure we still close the modal on error
      onAction({ type: 'CLOSE_MODAL' });
    }
  };

  // Dismiss/Got It handler: *only* close the modal
  const handleClose = () => {
    try {
      console.log('💬 [DemoModalContainer] handling dismiss/close');
      onAction({ type: 'CLOSE_MODAL' });
    } catch (error) {
      console.error('❌ [DemoModalContainer] Error handling close:', error);
      // Force modal closure even on error
      setState(prev => ({ ...prev, activeModal: null }));
    }
  };

  // Combine context-provided onClose with our local handler
  const combinedClose = () => {
    const contextClose = activeModal?.props?.onClose;
    if (contextClose) {
      contextClose();
    } else {
      handleClose();
    }
  };

  // Special handler for Smart Focus phase advancement
  const handleSmartFocusPhaseAdvance = () => {
    console.log('💬 [DemoModalContainer] Smart Focus phase advance called');
    console.log('💬 Current modal props:', activeModal?.props);
    if (activeModal?.props?.onAdvancePhase) {
      console.log('💬 Calling onAdvancePhase');
      activeModal.props.onAdvancePhase();
    } else {
      console.warn('💬 No onAdvancePhase handler found');
    }
  };

  return (
    <>
      {/* Only render modal if it exists */}
      {activeModal && (() => {
        const template = MODAL_TEMPLATES[activeModal.type] || {};
        const { component: CustomComponent } = template;

        // Merge template defaults with instance props
        const modalProps = {
          ...template,
          ...activeModal.props,
          modalType: activeModal.type, // Explicitly pass modalType
          isOpen: true,
          onAction: activeModal.type === 'SMART_FOCUS' ? activeModal.props.onAction : handlePrimaryAction,
          onClose: activeModal.type === 'SMART_FOCUS' ? activeModal.props.onClose : (activeModal.props.onClose || (template.isAcknowledgmentModal ? handlePrimaryAction : combinedClose)),
          onAdvancePhase: activeModal.type === 'SMART_FOCUS' ? handleSmartFocusPhaseAdvance : activeModal.props.onAdvancePhase,
          transcript,
          isRecording,
          isPaused,
          addTranscriptLine,
          updateTranscript,
          currentStep,
          totalSteps,
          demoProgress
        };

        console.log('💬 [DemoModalContainer] about to render:', { 
          activeModal, 
          modalProps,
          hasCustomComponent: !!CustomComponent,
          componentType: CustomComponent?.name || 'none'
        });

        return CustomComponent 
          ? <CustomComponent {...modalProps} />
          : <DemoModal {...modalProps} />;
      })()}

      {/* Always render spotlight if it exists */}
      {activeSpotlight && (
        <SpotlightOverlay
          data={activeSpotlight}
          onClose={onEffectComplete}
        />
      )}
    </>
  );
};

// Simple debug overlay to verify context values
const DemoDebugOverlay = () => {
  const { selectedJourney, demoStep, activeModal, pendingEffects, isPaused } = useDemo();
  
  return (
    <pre
      style={{
        position: 'fixed',
        bottom: 10,
        right: 10,
        background: 'rgba(0,0,0,0.8)',
        color: '#00ff00',
        padding: 10,
        borderRadius: 4,
        fontSize: 12,
        fontFamily: 'monospace',
        zIndex: 9999,
        pointerEvents: 'none',
        maxWidth: 400,
        maxHeight: 300,
        overflow: 'auto'
      }}
    >
      Demo Debug:
      {JSON.stringify({
        selectedJourney,
        demoStep,
        activeModal: activeModal ? {
          type: activeModal.type,
          hasNextAction: !!activeModal.nextAction
        } : null,
        pendingEffectsCount: pendingEffects?.length || 0,
        isPaused
      }, null, 2)}
    </pre>
  );
};

// Selection modal for protected routes
const DemoSelectionWrapper = () => {
  const { isDemoMode, selectedJourney, startDemo, resetDemo } = useDemo();

  return (
    <Routes>
      {/* Home Dashboard */}
      <Route path="/home-dashboard" element={<HomeDashboard />} />

      {/* Advisor Portal Routes */}
      <Route path="/advisor-portal">
        <Route index element={<Navigate to="/home-dashboard" replace />} />
        <Route path="annual-review-demo" element={<AnnualReviewDemo />} />
        {/* Client Database Section */}
        <Route
          path="client-database"
          element={<Clients />}
        />
        <Route
          path="client-database/household-profile/:householdId"
          element={<HouseholdProfile />}
        />
        <Route
          path="client-database/household-profile/:householdId/ar-profile/:year/:version"
          element={<AnnualReviewProfile />}
        />
        <Route
          path="client-database/household-profile/:householdId/ar-profile/:year/:version/post-review"
          element={
            <PostAnnualReviewActionItems />
          }
        />
        <Route
          path="client-database/household-profile/:householdId/client-profile/:userId/*"
          element={<ClientProfile />}
        >
          <Route
            index
            element={
              <Navigate
                to="household-information"
                replace
              />
            }
          />
          <Route
            path="household-information"
            element={<HouseholdInformation />}
          />
          <Route
            path="career-education"
            element={<CareerEducation />}
          />
          <Route
            path="health-life"
            element={<HealthLife />}
          />
          <Route
            path="income-benefits"
            element={<IncomeBenefits />}
          />
          <Route
            path="expenses-budgets"
            element={<ExpensesBudgets />}
          />
          <Route
            path="balance-sheet"
            element={<BalanceSheet />}
          />
          <Route
            path="goals-legacy"
            element={<GoalsLegacyWrapper />}
          />
          <Route
            path="summary-action-items"
            element={<SummaryActionItems />}
          />
        </Route>
        <Route
          path="client-database/household-profile/:householdId/communications"
          element={<Communications />}
        />
        <Route
          path="client-database/household-profile/:householdId/documents"
          element={<DocumentCenter />}
        />
        <Route
          path="client-database/household-profile/:householdId/calculators/rmd"
          element={<RMDAnalysis />}
        />
        <Route
          path="client-database/household-profile/:householdId/calculators/:calculatorId"
          element={<FinancialCalculators />}
        />
        <Route
          path="client-database/household-profile/:householdId/calculators"
          element={<FinancialCalculators />}
        />
        <Route
          path="client-database/household-profile/:householdId/relationship"
          element={<RelationshipManagement />}
        />
        <Route
          path="client-database/household-profile/:householdId/analysis"
          element={
            <AdvisorPortalAnalysisReports />
          }
        />
        <Route
          path="client-database/household-profile/:householdId/investments"
          element={<Investments />}
        />
        <Route
          path="client-database/household-profile/:householdId/tax-planning"
          element={<AdvisorPortalTaxPlanning />}
        />
        <Route
          path="client-database/household-profile/:householdId/banking"
          element={<Banking />}
        />
        <Route
          path="client-database/household-profile/:householdId/estate-planning"
          element={<EstatePlanning />}
        />
        <Route
          path="client-database/household-profile/:householdId/insurance"
          element={<Insurance />}
        />

        {/* Rest of advisor portal routes */}
        <Route
          path="calendar-events"
          element={<Calendar />}
        />
        <Route
          path="messages"
          element={<Messages />}
        />
        <Route path="email" element={<Email />} />
        <Route
          path="settings"
          element={<Settings />}
        />
        <Route
          path="client-onboarding"
          element={<ClientOnboarding />}
        />
        <Route
          path="events"
          element={<Events />}
        />
        <Route
          path="events/:eventId"
          element={<EventDetails />}
        />
        <Route
          path="events/all"
          element={<AllEvents />}
        />
      </Route>

      {/* Portfolio Management Routes */}
      <Route path="/portfolio-management">
        {alternativeInvestmentRoutes.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
        <Route
          path="firm-allocations"
          element={<FirmView />}
        />
        <Route
          path="advisor-allocations"
          element={<AdvisorView />}
        />
        <Route
          path="household-allocations"
          element={<HouseholdView />}
        />
        <Route
          path="household-allocations/:householdId"
          element={<HouseholdDetailViewWrapper />}
        />
        <Route
          path="account-allocations"
          element={<AccountView />}
        />
        <Route
          path="account-allocations/:accountNumber"
          element={<AccountView />}
        />
        <Route path="allocations">
          <Route
            index
            element={
              <Navigate
                to="/portfolio-management/firm-allocations"
                replace
              />
            }
          />
          <Route
            path="firm"
            element={
              <Navigate
                to="/portfolio-management/firm-allocations"
                replace
              />
            }
          />
          <Route
            path="advisor"
            element={
              <Navigate
                to="/portfolio-management/advisor-allocations"
                replace
              />
            }
          />
          <Route
            path="household/:id"
            element={
              <Navigate
                to="/portfolio-management/household-allocations"
                replace
              />
            }
          />
          <Route
            path="account/:id"
            element={
              <Navigate
                to="/portfolio-management/account-allocations"
                replace
              />
            }
          />
          <Route
            path="aum-upload"
            element={<AUMUpload />}
          />
        </Route>
        <Route
          path="market-news"
          element={<MarketNews />}
        />
        <Route
          path="market-research"
          element={<MarketResearch />}
        />
        <Route
          path="portfolio-reports"
          element={<PortfolioReports />}
        />
        <Route
          path="trading-rebalancing"
          element={<TradingRebalancing />}
        />
        <Route
          path="withdrawals-distributions"
          element={<WithdrawalsDistributions />}
        />
        <Route
          path="billing"
          element={<Billing />}
        />
      </Route>

      {/* Marketing & Sales Routes */}
      <Route path="/marketing-sales">
        <Route
          path="sales-coach"
          element={<SalesCoach />}
        />
        <Route
          path="campaigns"
          element={<Campaigns />}
        />
        <Route
          path="social-media-marketing"
          element={<SocialMediaMarketing />}
        />
        <Route
          path="design-hub"
          element={<DesignHub />}
        />
        <Route
          path="marketing-analytics"
          element={<MarketingAnalytics />}
        />
      </Route>

      {/* Administrative Routes */}
      <Route path="/administrative">
        <Route
          path="firm-overview"
          element={<FirmOverview />}
        />
        <Route
          path="company-structure"
          element={<CompanyStructure />}
        />
        <Route
          path="branding"
          element={<Branding />}
        />
        <Route
          path="compliance-center"
          element={<ComplianceCenter />}
        />
        <Route
          path="payroll-tax"
          element={<PayrollTax />}
        />
        <Route
          path="firm-analytics"
          element={<FirmAnalytics />}
        />
        <Route
          path="settings"
          element={<Settings />}
        />
        <Route
          path="firm-settings"
          element={<FirmSettings />}
        />
      </Route>

      {/* Education & Licensing Routes */}
      <Route path="/education-licensing">
        <Route
          path="financial-literacy"
          element={<FinancialLiteracy />}
        />
        <Route
          path="financial-news"
          element={<FinancialNews />}
        />
        <Route
          path="licenses-insurance"
          element={<CurrentLicenses />}
        />
        <Route
          path="insurance-coverage"
          element={<InsuranceCoverage />}
        />
        <Route
          path="ce-requirements"
          element={<CERequirements />}
        />
      </Route>

      {/* Financial Planning Routes */}
      <Route path="/financial-planning">
        <Route
          index
          element={
            <Navigate
              to="/home-dashboard"
              replace
            />
          }
        />
        <Route
          path="insurance"
          element={<Insurance />}
        />
        <Route
          path="banking"
          element={<Banking />}
        />
        <Route
          path="tax-planning"
          element={
            <FinancialPlanningTaxPlanning />
          }
        />
        <Route
          path="estate-planning"
          element={<EstatePlanning />}
        />
        <Route
          path="financial-calculators"
          element={<FinancialCalculators />}
        />
        <Route
          path="financial-calculators/:householdId"
          element={<FinancialCalculators />}
        />
        <Route
          path="financial-calculators/:householdId/:calculatorId"
          element={<FinancialCalculators />}
        />
        <Route
          path="financial-calculators/efficient-distribution"
          element={<EfficientDistribution />}
        />
        <Route
          path="analysis-reports"
          element={
            <FinancialPlanningAnalysisReports />
          }
        />
        <Route
          path="retirement-journey"
          element={<RetirementJourney />}
        />
      </Route>

      {/* Redirect dashboard to home */}
      <Route path="/dashboard" element={<Navigate to="/home-dashboard" />} />

      {/* Catch-all for authenticated users */}
      <Route path="*" element={<Navigate to="/home-dashboard" replace />} />
    </Routes>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* Protected Routes - First check auth, then wrap in AppLayout */}
      <Route element={<PrivateRoute />}>
        <Route element={<AppLayout />}>
          <Route path="*" element={<DemoSelectionWrapper />} />
        </Route>
      </Route>
    </Routes>
  );
};

const App = () => {
  return (
    <>
      <AppRoutes />
      <DemoModalContainer />
      <SpotlightOverlay />
    </>
  );
};

export default App;